import { useState, useEffect, useRef } from 'react';
import { Calendar, Play, Star, Award, Users, Sparkles, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  BootcampFeatures,
  BootcampCard,
  BootcampDetailModal,
  BootcampBookingModal
} from '../components/bootcamp';
import SEO from '../components/common/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Video and image assets
const BOOTCAMP_VIDEO = "https://customer-assets.emergentagent.com/job_bfb4c33f-5801-45fc-b2e5-0237443f0ff9/artifacts/y68e9qr5_bootcamp%20ad.mov";
const BOOTCAMP_HISTORY_IMAGE = "https://customer-assets.emergentagent.com/job_bfb4c33f-5801-45fc-b2e5-0237443f0ff9/artifacts/q8x5ibz2_Screenshot%202026-03-04%20at%2009.33.27.png";

const WestendBootcamps = () => {
  const [bootcamps, setBootcamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBootcamp, setSelectedBootcamp] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchBootcamps();
  }, []);

  const fetchBootcamps = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bootcamps`);
      setBootcamps(res.data);
    } catch (error) {
      console.error('Failed to fetch bootcamps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (bootcamp) => {
    setSelectedBootcamp(bootcamp);
    setDetailModalOpen(true);
  };

  const handleBook = (bootcamp) => {
    setSelectedBootcamp(bootcamp);
    setBookingModalOpen(true);
    setDetailModalOpen(false);
  };

  const handleBookingSuccess = () => {
    fetchBootcamps();
  };

  const scrollToList = () => {
    document.getElementById('bootcamps-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO page="camps" />
      <Navbar />
      
      {/* Epic Hero Section with Video */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            className="w-full h-full object-cover opacity-40"
            src={BOOTCAMP_VIDEO}
            loop
            muted
            playsInline
            autoPlay
            onPlay={() => setVideoPlaying(true)}
            onPause={() => setVideoPlaying(false)}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-purple-900/40 to-black" />
        </div>
        
        {/* Animated Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10 z-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239333ea' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 text-sm mb-6 animate-pulse">
              Summer 2026 - Limited Spots!
            </Badge>
            
            <h1 className="text-6xl md:text-8xl font-black text-white mb-4 tracking-tight">
              WEST END
              <span className="block bg-gradient-to-r from-purple-400 via-pink-500 to-amber-400 bg-clip-text text-transparent">
                BOOTCAMPS
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-8">
              Train with <span className="text-pink-400 font-semibold">real West End performers</span> in an intensive week of singing, dancing, and acting
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-10">
              <div className="text-center">
                <p className="text-4xl font-bold text-pink-400">12+</p>
                <p className="text-white/60 text-sm">Years Running</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-purple-400">500+</p>
                <p className="text-white/60 text-sm">Students Trained</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-amber-400">20+</p>
                <p className="text-white/60 text-sm">West End Stars</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white text-lg px-10 py-7 rounded-full shadow-lg shadow-pink-500/30"
                onClick={scrollToList}
                data-testid="book-spot-btn"
              >
                <Sparkles className="mr-2 w-5 h-5" />
                Book Your Spot
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 text-lg px-10 py-7 rounded-full"
                onClick={toggleVideo}
              >
                <Play className={`mr-2 w-5 h-5 ${videoPlaying ? 'text-pink-400' : ''}`} />
                {videoPlaying ? 'Pause Video' : 'Watch Promo'}
              </Button>
            </div>
          </motion.div>
          
          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="w-8 h-8 text-white/50" />
          </motion.div>
        </div>
      </section>

      {/* History Showcase Section */}
      <section className="py-20 bg-gradient-to-b from-black via-purple-950/30 to-black overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              A Legacy of <span className="text-pink-400">Excellence</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Over 12 years of bringing West End magic to Milton Keynes. From SIX to Hamilton, 
              Shrek to Chicago - our students learn from the very best.
            </p>
          </motion.div>
          
          {/* History Image Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative max-w-6xl mx-auto"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-pink-500 via-purple-500 to-amber-500 rounded-2xl opacity-30 blur-xl" />
            <div className="relative rounded-xl overflow-hidden border-2 border-white/10 shadow-2xl">
              <img 
                src={BOOTCAMP_HISTORY_IMAGE} 
                alt="West End Bootcamp History - 7 Years of Training with West End Stars"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-pink-500/80 text-white px-4 py-1">SIX The Musical</Badge>
                  <Badge className="bg-purple-500/80 text-white px-4 py-1">Hamilton</Badge>
                  <Badge className="bg-amber-500/80 text-white px-4 py-1">Chicago</Badge>
                  <Badge className="bg-blue-500/80 text-white px-4 py-1">Shrek</Badge>
                  <Badge className="bg-green-500/80 text-white px-4 py-1">Dancing on Ice</Badge>
                  <Badge className="bg-red-500/80 text-white px-4 py-1">Emmerdale</Badge>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-gradient-to-br from-pink-500/10 to-transparent border-pink-500/20 h-full">
                <CardContent className="p-6 text-center">
                  <Star className="w-12 h-12 text-pink-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">West End Professionals</h3>
                  <p className="text-white/60">Learn directly from performers who have starred in London's biggest shows, as well as TV and Film</p>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20 h-full">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">End of Week Showcase</h3>
                  <p className="text-white/60">Perform on stage at the end of the week for friends and family</p>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20 h-full">
                <CardContent className="p-6 text-center">
                  <Users className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">All Abilities Welcome</h3>
                  <p className="text-white/60">From beginners to advanced performers, everyone is welcome</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <BootcampFeatures />
      
      {/* Available Bootcamps */}
      <section id="bootcamps-list" className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Available <span className="text-purple-500">Bootcamps</span>
          </h2>
          <p className="text-center text-white/60 mb-12">
            Secure your spot for an unforgettable week
          </p>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : bootcamps.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 text-lg">No bootcamps currently available</p>
              <p className="text-white/30 mt-2">Check back soon for upcoming dates!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bootcamps.map((bootcamp) => (
                <BootcampCard
                  key={bootcamp.id}
                  bootcamp={bootcamp}
                  onViewDetails={() => handleViewDetails(bootcamp)}
                  onBook={() => handleBook(bootcamp)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900/50 via-pink-900/50 to-purple-900/50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Live Your West End Dream?
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of young performers who have trained with the best in the business
          </p>
          <Button 
            size="lg"
            className="bg-white text-purple-900 hover:bg-white/90 text-lg px-10 py-7 rounded-full font-bold"
            onClick={scrollToList}
          >
            View Available Dates
          </Button>
        </div>
      </section>

      {/* Detail Modal */}
      <BootcampDetailModal
        bootcamp={selectedBootcamp}
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onBook={() => handleBook(selectedBootcamp)}
      />

      {/* Booking Modal */}
      <BootcampBookingModal
        bootcamp={selectedBootcamp}
        open={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        onSuccess={handleBookingSuccess}
      />
    </div>
  );
};

export default WestendBootcamps;
